import { useState, useEffect, useCallback } from 'react';
import ClientsTab from '../People/ClientsTab';
import { useNavigate } from 'react-router-dom';
import ProvisionOrgWizard from './ProvisionOrgWizard';
import ImpersonateModal from './ImpersonateModal';
import FederationModal from './FederationModal';
import OrgLifecycleModal from './OrgLifecycleModal';
import useImpersonation from '../../hooks/useImpersonation';

const API = process.env.REACT_APP_API_URL || '';

/**
 * Platform Admin Console — Stage 3 of the user-management revamp.
 *
 * Only accessible to user_type === 'platform_superuser' (the support@ account).
 * Lists all customer organizations and lets you provision new ones via the
 * ProvisionOrgWizard.
 */
export default function PlatformAdmin() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orgs, setOrgs] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [toast, setToast] = useState(null);
  const [impersonateOrg, setImpersonateOrg] = useState(null);
  const [federationOrg, setFederationOrg] = useState(null);
  const [clientsOrg, setClientsOrg] = useState(null); // TPA whose clients panel is open
  // Catalog (product list) — used by the per-org "Add product" picker so
  // operators can attach a previously-unbought product (memo, cap, etc.)
  // to an existing org without re-provisioning it.
  const [catalog, setCatalog] = useState([]);
  const [addingProductOrg, setAddingProductOrg] = useState(null);
  const [addProductBusy, setAddProductBusy] = useState(false);
  // Lifecycle (offboarding) — status badge map + open modal target.
  const [lifecycle, setLifecycle] = useState({});
  const [lifecycleOrg, setLifecycleOrg] = useState(null);
  const navigate = useNavigate();
  const impersonation = useImpersonation();

  // ─── Session check ───
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/auth/session`, { credentials: 'include' });
        const data = await res.json();
        if (!data.success) {
          navigate('/login');
          return;
        }
        // Gate by effective_user_type so this works even before backfill
        const allowedTypes = ['platform_superuser'];
        const isAllowed =
          allowedTypes.includes(data.user.user_type) ||
          allowedTypes.includes(data.user.effective_user_type) ||
          data.user.role === 'superadmin';
        if (!isAllowed) {
          navigate('/');
          return;
        }
        setUser(data.user);
      } catch (err) {
        navigate('/login');
      }
      setLoading(false);
    })();
  }, [navigate]);

  // ─── Org list ───
  const fetchOrgs = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/v2/orgs`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setOrgs(data.organizations || []);
    } catch (err) {
      console.error('Failed to load orgs:', err);
    }
    setOrgsLoading(false);
  }, []);

  // ─── Lifecycle status badges (offboarding) ───
  const fetchLifecycle = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/v2/lifecycle/overview`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setLifecycle(data.overview || {});
    } catch (err) {
      console.error('Failed to load lifecycle overview:', err);
    }
  }, []);

  // ─── Catalog (for Add Product picker) ───
  const fetchCatalog = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/v2/catalog/apps`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setCatalog(data.apps || []);
    } catch (err) {
      console.error('Failed to load catalog:', err);
    }
  }, []);

  useEffect(() => {
    if (user) { fetchOrgs(); fetchCatalog(); fetchLifecycle(); }
  }, [user, fetchOrgs, fetchCatalog, fetchLifecycle]);

  // Add a product to an existing org. Re-runs the bridge for every user
  // in the org so they pick up the new product's app_roles entries.
  const handleAddProduct = async (org, productName) => {
    setAddProductBusy(true);
    try {
      const res = await fetch(`${API}/api/admin/v2/orgs/${org.id}/products`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_name: productName }),
      });
      const data = await res.json();
      if (data.success) {
        setToast({
          type: 'success',
          title: data.already
            ? `${productName} was already active for ${org.name}`
            : `${productName} added to ${org.name}`,
          body: data.already
            ? 'Nothing changed — the product was already in the org.'
            : `Bridge re-ran for ${data.bridged} user(s)${data.bridge_errors ? `, ${data.bridge_errors} error(s)` : ''}. Users need to sign out and back in to refresh their session.`,
        });
        setAddingProductOrg(null);
        fetchOrgs();
      } else {
        setToast({ type: 'error', title: 'Failed to add product', body: data.message || 'Unknown error' });
      }
    } catch (err) {
      setToast({ type: 'error', title: 'Network error', body: err.message });
    }
    setAddProductBusy(false);
  };

  // ─── Toast auto-dismiss ───
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 8000);
    return () => clearTimeout(t);
  }, [toast]);

  // 2026-08-24 role redesign: platform support opens an org's compliance
  // apps only through a superuser-approved, time-boxed window. This files
  // the request; the org's super user decides on their People page.
  const requestSupportAccess = async (org) => {
    const reason = window.prompt(`Reason for requesting access to ${org.name} (the super user sees this):`);
    if (!reason) return;
    const hoursRaw = window.prompt('Access window in hours (1–168):', '24');
    if (hoursRaw === null) return;
    try {
      const res = await fetch(`${API}/api/support-access/requests`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ organization_id: org.id, reason, duration_hours: Number(hoursRaw) || 24 }),
      });
      const data = await res.json();
      if (data.success) {
        setToast({ type: 'success', title: 'Access requested', body: `${org.name}'s super user must approve before compliance apps open.` });
      } else {
        setToast({ type: 'error', title: 'Request not filed', body: data.message || 'Unknown error' });
      }
    } catch (e) {
      setToast({ type: 'error', title: 'Request not filed', body: e.message });
    }
  };

  const handleProvisioned = (result) => {
    setShowWizard(false);
    let body;
    if (result.identity_mode && result.identity_mode !== 'local') {
      if (result.federation_email_sent) {
        body = `Microsoft sign-in email sent to ${result.first_admin.email}`;
      } else {
        body = `Org created (Microsoft sign-in). Email failed: ${result.federation_email_error || 'unknown error'} — open Identity to resend.`;
      }
    } else if (result.invite_sent) {
      body = `Welcome email sent to ${result.first_admin.email}`;
    } else {
      body = `Account created for ${result.first_admin.email} (no invite sent)`;
    }
    setToast({
      type: result.identity_mode !== 'local' && !result.federation_email_sent ? 'error' : 'success',
      title: `${result.organization.name} provisioned`,
      body,
    });
    fetchOrgs();
  };

  if (loading) return <FullPageSpinner label="Loading…" />;
  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
      {/* Header */}
      <header className="border-b border-slate-700/50 bg-slate-900/60 backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center shadow-lg">
              <span className="text-white font-extrabold text-[10px] tracking-[0.15em]">PCG</span>
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight">Platform Admin Console</h1>
              <p className="text-xs text-slate-500">Provision and manage customer organizations</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs text-slate-500">{user.email}</span>
            <button
              onClick={() => navigate('/app')}
              className="text-xs text-slate-400 hover:text-white transition"
            >
              Switch to App →
            </button>
            <button
              onClick={() => {
                fetch(`${API}/api/auth/logout`, {
                  method: 'GET',
                  credentials: 'include',
                }).then(() => {
                  localStorage.removeItem('sessionUser');
                  localStorage.removeItem('lastPlatform');
                  window.location.href = '/login';
                });
              }}
              className="px-3 py-1.5 rounded-lg bg-red-600/20 border border-red-500/40 text-red-200 hover:bg-red-600/30 hover:text-white text-xs font-semibold transition"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      {/* Toast */}
      {toast && (
        <div className="fixed top-20 right-6 z-50 w-96">
          <div className={`rounded-xl border shadow-2xl p-4 backdrop-blur-xl ${
            toast.type === 'success'
              ? 'bg-emerald-500/15 border-emerald-500/40'
              : 'bg-red-500/15 border-red-500/40'
          }`}>
            <div className="flex items-start gap-3">
              <span className="text-2xl">{toast.type === 'success' ? '✓' : '⚠'}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white">{toast.title}</p>
                <p className="text-xs text-slate-300 mt-0.5">{toast.body}</p>
              </div>
              <button onClick={() => setToast(null)} className="text-slate-400 hover:text-white text-xl leading-none">×</button>
            </div>
          </div>
        </div>
      )}

      {/* Body */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Active impersonation banner — shown when a session is already running */}
        {impersonation.active && (
          <div className="mb-6 rounded-2xl border border-red-500/40 bg-gradient-to-r from-red-600/20 to-red-700/10 px-5 py-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <span className="text-2xl" aria-hidden="true">&#128065;</span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-white">
                  Currently impersonating{' '}
                  <span className="underline decoration-red-300 underline-offset-2">
                    {impersonation.target_org?.name || 'a client organization'}
                  </span>
                </p>
                <p className="text-xs text-red-200/90 mt-0.5">
                  Read-only · {impersonation.minutesRemaining !== null ? `${impersonation.minutesRemaining} min remaining` : 'session active'}
                  {impersonation.reason ? ` · "${impersonation.reason}"` : ''}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => { window.location.href = '/app'; }}
                className="px-3 py-1.5 rounded-lg bg-white text-red-700 text-xs font-semibold hover:bg-red-50 transition"
              >
                Go to active session
              </button>
              <button
                onClick={async () => {
                  if (!window.confirm('End the active impersonation session?')) return;
                  await impersonation.endImpersonation();
                }}
                className="px-3 py-1.5 rounded-lg bg-red-700/40 border border-red-400/50 text-white text-xs font-semibold hover:bg-red-700/60 transition"
              >
                End session
              </button>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <StatCard label="Total Organizations" value={orgs.filter((o) => o.account_kind !== 'tpa_client').length} />
          <StatCard label="Setup Complete" value={orgs.filter(o => o.setup_complete).length} />
          <StatCard label="Total Active Users" value={orgs.reduce((sum, o) => sum + Number(o.active_user_count || 0), 0)} />
        </div>

        {/* Action bar */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Organizations</h2>
          <button
            onClick={() => setShowWizard(true)}
            className="px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-500 rounded-lg font-medium text-sm text-white hover:shadow-lg hover:shadow-blue-500/25 transition"
          >
            + New Organization
          </button>
        </div>

        {/* Org table */}
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl overflow-hidden">
          {orgsLoading ? (
            <div className="px-6 py-12 text-center text-slate-500">Loading organizations…</div>
          ) : orgs.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <p className="text-slate-400 mb-2">No organizations yet</p>
              <p className="text-xs text-slate-500">Click + New Organization to provision your first customer</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-900/40 border-b border-slate-700/50">
                <tr>
                  <th className="text-left text-xs uppercase tracking-wide text-slate-400 font-semibold px-6 py-3">Organization</th>
                  <th className="text-left text-xs uppercase tracking-wide text-slate-400 font-semibold px-6 py-3">Active Users</th>
                  <th className="text-left text-xs uppercase tracking-wide text-slate-400 font-semibold px-6 py-3">Products</th>
                  <th className="text-left text-xs uppercase tracking-wide text-slate-400 font-semibold px-6 py-3">Setup</th>
                  <th className="text-left text-xs uppercase tracking-wide text-slate-400 font-semibold px-6 py-3">Provisioned</th>
                  <th className="text-right text-xs uppercase tracking-wide text-slate-400 font-semibold px-6 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const clientsOf = (id) => orgs.filter((o) => o.account_kind === 'tpa_client' && o.parent_organization_id === id);
                  const ordered = [];
                  for (const o of orgs) {
                    if (o.account_kind === 'tpa_client') continue;
                    ordered.push(o);
                    if (o.account_kind === 'tpa') ordered.push(...clientsOf(o.id));
                  }
                  // Orphans (a client whose TPA is not in the list) still render.
                  for (const o of orgs) if (o.account_kind === 'tpa_client' && !ordered.includes(o)) ordered.push(o);
                  return ordered;
                })().map(org => (
                  <tr key={org.id} className={`border-b border-slate-700/30 hover:bg-slate-700/20 transition ${org.account_kind === 'tpa_client' ? 'bg-slate-900/30' : ''}`}>
                    <td className={org.account_kind === 'tpa_client' ? 'pl-14 pr-6 py-3 border-l-4 border-sky-500/40' : 'px-6 py-4'}>
                      <div className="flex items-center gap-2">
                        {org.account_kind === 'tpa_client' && <span className="text-slate-500 text-sm" aria-hidden="true">↳</span>}
                        <span className={org.account_kind === 'tpa_client' ? 'font-medium text-slate-200' : 'font-medium text-white'}>{org.name}</span>
                        {org.account_kind === 'tpa' && (
                          <>
                            <span className="text-[10px] px-1.5 py-0.5 rounded uppercase font-bold tracking-wide bg-sky-500/20 text-sky-300 border border-sky-500/40">Third-party administrator</span>
                            <button
                              onClick={() => setClientsOrg(org)}
                              className="text-[11px] px-2 py-0.5 rounded-lg bg-sky-600/80 hover:bg-sky-500 text-white font-medium transition"
                              title="Add or remove the health plans this administrator serves"
                            >
                              Clients ({orgs.filter((o) => o.parent_organization_id === org.id && (lifecycle[o.id]?.lifecycle_status || 'active') === 'active').length})
                            </button>
                          </>
                        )}
                        {org.account_kind === 'tpa_client' && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded uppercase font-bold tracking-wide bg-slate-700/60 text-slate-300 border border-slate-600" title="A client workspace inside a third-party administrator's account">
                            Client workspace{org.cms_contract_number ? ` · ${org.cms_contract_number}` : ''}
                          </span>
                        )}
                        <LifecycleBadge state={lifecycle[org.id]} />
                      </div>
                      <div className="text-xs text-slate-500 font-mono">
                        {org.id.slice(0, 8)}…
                        {org.account_kind === 'tpa_client' && org.parent_organization_id && (
                          <span className="ml-2 text-slate-500">
                            of {orgs.find((o) => o.id === org.parent_organization_id)?.name || 'a third-party administrator'}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-300">{org.active_user_count || 0}</td>
                    <td className="px-6 py-4">
                      {Array.isArray(org.products) && org.products.length > 0 ? (
                        <div className="flex flex-wrap gap-1 max-w-xs">
                          {org.products.map((p) => (
                            <span
                              key={p}
                              className={`text-[10px] px-1.5 py-0.5 rounded uppercase font-bold tracking-wide ${
                                p === 'memo'
                                  ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                                  : 'bg-slate-700/60 text-slate-300 border border-slate-600'
                              }`}
                            >
                              {p}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-500">{org.product_count || 0}</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {org.setup_complete ? (
                        <span className="text-xs bg-emerald-500/10 text-emerald-400 px-2 py-1 rounded-full border border-emerald-500/20">Complete</span>
                      ) : (
                        <span className="text-xs bg-amber-500/10 text-amber-400 px-2 py-1 rounded-full border border-amber-500/20">Pending</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-xs text-slate-500">
                      {org.created_at ? new Date(org.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setImpersonateOrg(org)}
                          disabled={impersonation.active}
                          title={impersonation.active ? 'End the current impersonation session first' : `View as ${org.name}`}
                          className="px-3 py-1.5 bg-red-500/15 hover:bg-red-500/25 border border-red-500/40 rounded-lg text-xs font-medium text-red-300 transition disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          View as
                        </button>
                        <button
                          onClick={() => setFederationOrg(org)}
                          title={`Identity & sign-in for ${org.name}`}
                          className="px-3 py-1.5 bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/40 rounded-lg text-xs font-medium text-amber-300 transition"
                        >
                          Identity
                        </button>
                        <button
                          onClick={() => setAddingProductOrg(org)}
                          title={`Add a product (Mandate, CAP, Scrubber, ...) to ${org.name}`}
                          className="px-3 py-1.5 bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/40 rounded-lg text-xs font-medium text-emerald-300 transition"
                        >
                          + Product
                        </button>
                        <button
                          onClick={() => navigate(`/people?org=${org.id}`)}
                          className="px-3 py-1.5 bg-blue-500/15 hover:bg-blue-500/25 border border-blue-500/40 rounded-lg text-xs font-medium text-blue-300 transition"
                        >
                          Manage Users →
                        </button>
                        <button
                          onClick={() => setLifecycleOrg(org)}
                          title={`Suspend or retire ${org.name} (contract offboarding)`}
                          className="px-3 py-1.5 bg-slate-600/30 hover:bg-slate-600/50 border border-slate-500/40 rounded-lg text-xs font-medium text-slate-200 transition"
                        >
                          Lifecycle
                        </button>
                        <button
                          onClick={() => requestSupportAccess(org)}
                          title={`Ask ${org.name}'s super user to open a time-boxed support-access window. You cannot open their compliance apps without it.`}
                          className="px-3 py-1.5 bg-purple-500/15 hover:bg-purple-500/25 border border-purple-500/40 rounded-lg text-xs font-medium text-purple-300 transition"
                        >
                          Request access
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>

      {/* Provision wizard modal */}
      {showWizard && (
        <ProvisionOrgWizard
          onClose={() => setShowWizard(false)}
          onProvisioned={handleProvisioned}
        />
      )}

      {/* Impersonation modal */}
      {impersonateOrg && (
        <ImpersonateModal
          org={impersonateOrg}
          onClose={() => setImpersonateOrg(null)}
        />
      )}

      {/* Add Product picker — lets a Platform Superuser attach an additional
          product to an existing org. After insert, the server re-runs the
          legacyAppRoleBridge for every user in the org so they pick up the
          new product's user_app_roles entries (memo → dept-scoped associate
          for dept users, etc.). */}
      {addingProductOrg && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 backdrop-blur-sm px-4"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setAddingProductOrg(null); }}
        >
          <div className="w-full max-w-lg bg-slate-900 border border-slate-700/70 rounded-2xl shadow-2xl">
            <div className="px-6 py-5 border-b border-slate-700/60">
              <h3 className="text-lg font-bold text-white">Add product to {addingProductOrg.name}</h3>
              <p className="text-xs text-slate-400 mt-1">
                Pick a product the org didn't buy at provisioning time. The bridge re-runs for every user so
                their app access updates immediately — they'll just need to sign out and back in.
              </p>
            </div>
            <div className="px-6 py-5 space-y-2 max-h-[60vh] overflow-y-auto">
              {catalog.length === 0 ? (
                <p className="text-sm text-slate-500">Loading catalog…</p>
              ) : catalog
                .filter((app) => !(addingProductOrg.products || []).includes(app.name))
                .map((app) => (
                  <button
                    key={app.name}
                    onClick={() => handleAddProduct(addingProductOrg, app.name)}
                    disabled={addProductBusy}
                    className="w-full text-left px-4 py-3 rounded-xl border border-slate-700 hover:border-emerald-500/60 hover:bg-emerald-500/10 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-white">{app.display_name}</p>
                        <p className="text-[11px] text-slate-500 mt-0.5">{app.name} · {app.category}</p>
                        {app.description && (
                          <p className="text-xs text-slate-400 mt-1 line-clamp-2">{app.description}</p>
                        )}
                      </div>
                      <span className="text-[10px] uppercase tracking-wide text-emerald-300 font-bold flex-shrink-0">
                        Add →
                      </span>
                    </div>
                  </button>
                ))}
              {catalog.length > 0
                && catalog.filter((app) => !(addingProductOrg.products || []).includes(app.name)).length === 0 && (
                <p className="text-sm text-slate-400">
                  This org already owns every product in the catalog.
                </p>
              )}
            </div>
            <div className="px-6 py-4 border-t border-slate-700/60 flex justify-end">
              <button
                onClick={() => setAddingProductOrg(null)}
                disabled={addProductBusy}
                className="px-4 py-2 rounded-lg text-sm text-slate-300 hover:bg-slate-800 transition disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lifecycle (offboarding) modal */}
      {lifecycleOrg && (
        <OrgLifecycleModal
          org={lifecycleOrg}
          onClose={() => setLifecycleOrg(null)}
          onChanged={() => { fetchOrgs(); fetchLifecycle(); }}
        />
      )}

      {/* Federation modal */}
      {clientsOrg && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center px-4 py-10 overflow-y-auto">
          <div className="w-full max-w-5xl rounded-2xl bg-slate-900 border border-slate-700 shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700/60">
              <div>
                <h2 className="text-lg font-semibold text-white">{clientsOrg.name}</h2>
                <p className="text-xs text-slate-400">Health plans this administrator serves. Each one is billed as a client.</p>
              </div>
              <button onClick={() => { setClientsOrg(null); fetchOrgs(); fetchLifecycle(); }} className="text-slate-400 hover:text-white text-2xl leading-none">×</button>
            </div>
            <div className="px-6 py-5">
              <ClientsTab tpaId={clientsOrg.id} showToast={(type, title, body) => setToast({ type, title, body })} />
            </div>
          </div>
        </div>
      )}
      {federationOrg && (
        <FederationModal
          org={federationOrg}
          onClose={() => setFederationOrg(null)}
          onSaved={({ org: o, mode }) => setToast({
            type: 'success',
            title: `${o.name} — identity saved`,
            body: mode === 'local'
              ? 'Mode set to PCG-managed. Federation is disabled for this org.'
              : `Mode set to ${mode}. Share the test sign-in URL with their super user.`,
          })}
        />
      )}
    </div>
  );
}

function LifecycleBadge({ state }) {
  const status = state?.lifecycle_status;
  if (!status || status === 'active') return null;
  if (status === 'suspended') {
    return <span className="text-[10px] px-1.5 py-0.5 rounded-full uppercase font-bold tracking-wide bg-amber-500/20 text-amber-300 border border-amber-500/40">Suspended</span>;
  }
  if (status === 'pending_purge') {
    return <span className="text-[10px] px-1.5 py-0.5 rounded-full uppercase font-bold tracking-wide bg-red-500/20 text-red-300 border border-red-500/40">Deletion scheduled</span>;
  }
  return null;
}

function StatCard({ label, value }) {
  return (
    <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-5">
      <div className="text-xs uppercase tracking-wide text-slate-400 font-semibold mb-2">{label}</div>
      <div className="text-3xl font-bold text-white">{value}</div>
    </div>
  );
}

function FullPageSpinner({ label }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900">
      <div className="text-center">
        <div className="w-12 h-12 mx-auto mb-4 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-slate-400 text-sm">{label}</p>
      </div>
    </div>
  );
}
